import type { Dataset } from "./types";

const FILES = [
  "meta",
  "customers",
  "projects",
  "branches",
  "billing_summary",
  "portfolio_mix",
  "kpi_enterprise",
  "ar_by_customer",
  "ar_aging",
  "dso_by_tier",
  "credit_events",
  "change_orders",
  "industry_commodities",
  "industry_macro",
  "industry_segments",
  "industry_news",
] as const;

const BASE = (import.meta as any).env?.BASE_URL ?? "/";

async function fetchJson<T>(name: string): Promise<T> {
  const url = `${BASE}data/${name}.json`.replace(/\/+/g, "/");
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to load ${url}: ${res.status}`);
  return (await res.json()) as T;
}

// Optional data files — absence must not break the app.
const OPTIONAL_FILES = ["ni_forecast"] as const;

export async function loadDataset(): Promise<Dataset> {
  const parts = await Promise.all(FILES.map((f) => fetchJson<any>(f)));
  const out: any = {};
  FILES.forEach((f, i) => (out[f] = parts[i]));
  // Optional files load best-effort; a missing file leaves the field undefined.
  await Promise.all(
    OPTIONAL_FILES.map(async (f) => {
      try {
        out[f] = await fetchJson<any>(f);
      } catch {
        out[f] = undefined;
      }
    }),
  );
  return out as Dataset;
}

export const fmt = {
  money(n: number, opts: { compact?: boolean; sign?: boolean } = {}): string {
    if (n == null || isNaN(n)) return "—";
    const sign = opts.sign && n > 0 ? "+" : "";
    if (opts.compact) {
      const abs = Math.abs(n);
      if (abs >= 1e9) return `${sign}$${(n / 1e9).toFixed(1)}B`;
      if (abs >= 1e6) return `${sign}$${(n / 1e6).toFixed(1)}M`;
      if (abs >= 1e3) return `${sign}$${(n / 1e3).toFixed(0)}K`;
      return `${sign}$${n.toFixed(0)}`;
    }
    return `${sign}$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  },
  pct(n: number, digits = 1): string {
    if (n == null || isNaN(n)) return "—";
    return `${(n * 100).toFixed(digits)}%`;
  },
  num(n: number, digits = 0): string {
    if (n == null || isNaN(n)) return "—";
    return n.toLocaleString("en-US", { maximumFractionDigits: digits });
  },
  days(n: number | null): string {
    if (n == null || isNaN(n as number)) return "—";
    return `${Math.round(n as number)}d`;
  },
};
